import * as React from "react";
import { DropdownMenu as M } from "radix-ui";

import { Check, ChevronRight, Filter, Search, SlidersHorizontal, X } from "../icons";
import { Button } from "../button";
import { Elevated } from "../elevated";
import { Popover, PopoverContent, PopoverTrigger } from "../popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../select";
import { Switch } from "../switch";
import { cn } from "../utils";

/**
 * The title-row collection controls — TWO menus, one shared vocabulary.
 *
 *   • `<FilterMenu>` (funnel) — WHAT rows do I see. A search-first "Add filter…"
 *     command list whose fields open their value pickers **on hover** (Radix
 *     `Sub`), each with its own search box. No click-to-drill, no back button.
 *   • `<DisplayMenu>` (sliders) — HOW do I see them. A flat panel: the view
 *     segments (List · Board · Calendar) on top, then label-led rows of compact
 *     selects and switches, then the display-property chips.
 *
 * They are separate because they answer different questions and want different
 * shapes — a menu of value pickers vs. a settings panel — but they share this
 * file, the `ViewMenuOption` vocabulary, and the surface/row primitives, so a
 * page describes both in DATA and never hand-rolls a popover again.
 *
 * What this replaces: `TasksFilterMenu` (a bespoke click-drill popover with a
 * hand-rolled category list + `<ArrowLeft>` back button, re-written per page) and
 * the standalone `<ViewSelect>` that sat beside it.
 *
 * ```tsx
 * <SectionActions primary={<CreateMenu />}>
 *   <FilterMenu fields={FILTER_FIELDS} onReset={clearAll} />
 *   <DisplayMenu views={VIEWS} sections={DISPLAY_SECTIONS} />
 * </SectionActions>
 * ```
 */

/* ============================================================ shared vocabulary */

export interface ViewMenuOption {
  value: string;
  label: string;
  /** Leading glyph. */
  icon?: React.ComponentType<{ className?: string }>;
  /** Custom leading mark that wins over `icon` (a StatusIcon, an avatar, a dot). */
  mark?: React.ReactNode;
  /** Right-aligned muted text — "1 issue", a shortcut. */
  hint?: string;
  /** Extra text the search box matches, beyond `label`. */
  keywords?: string;
}

/**
 * Shared lifted surface — root content, submenus, and the display panel.
 *
 * z-[90] matches the dropdown-menu atom, and must: this surface renders inside
 * `M.Portal`, so it lands on document.body as a SIBLING of Dialog/Sheet rather
 * than a descendant. Left at z-50 it painted under Dialog (z-[80]) and under
 * the AppsStage panel — the same portaled-peer bug that stranded Tooltip when
 * the six atoms were raised. A hand-rolled peer of an atom has to move with it.
 */
const SURFACE = cn(
  "z-[90] rounded-lg border border-border p-1 text-foreground",
  "data-[state=open]:animate-in data-[state=closed]:animate-out",
  "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
  "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
);

/** One row's geometry — items, sub-triggers, checkbox/radio rows all share it. */
const ROW = cn(
  "relative flex cursor-pointer select-none items-center gap-2 rounded px-2 py-1.5 type-body",
  "outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0",
  "data-[highlighted]:bg-foreground/[0.06] data-[highlighted]:text-foreground",
  "data-[state=open]:bg-foreground/[0.06]",
  "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
);

function Glyph({
  icon: Icon,
  mark,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  mark?: React.ReactNode;
}) {
  if (mark)
    return (
      <span className="inline-flex size-3.5 shrink-0 items-center justify-center">
        {mark}
      </span>
    );
  if (!Icon) return null;
  return <Icon className="size-3.5 shrink-0 text-muted-foreground" />;
}

function Summary({ children }: { children: React.ReactNode }) {
  return (
    <span className="ml-auto max-w-[8rem] truncate type-subtext text-muted-foreground">
      {children}
    </span>
  );
}

/**
 * The multi-select mark. A checkbox row shows an empty BOX when unchecked, not
 * blank space — otherwise a value list is indistinguishable from a radio list
 * until you have already clicked one, and the "you may pick several" affordance
 * arrives too late to be useful.
 */
function CheckBox({ checked }: { checked: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        "absolute left-2 flex size-3.5 items-center justify-center rounded-xs border transition-colors",
        // Checked takes the ACCENT, not foreground. A solid white box on a dark
        // menu is the brightest thing on screen and outshouts the row it belongs
        // to (Matt: "the checkboxes are white — looks wrong"). Selection is
        // meaning, which is exactly what the substrate reserves color for.
        checked
          ? "border-primary bg-primary text-primary-foreground"
          : "border-foreground/30",
      )}
    >
      {checked ? <Check className="size-2.5" strokeWidth={3} /> : null}
    </span>
  );
}

/**
 * Search box that lives INSIDE menu content. Radix menus run typeahead on every
 * keystroke and close on Escape — both have to be stopped at the input or you
 * cannot type a word without the menu jumping rows or vanishing.
 */
function MenuSearch({
  value,
  onChange,
  placeholder,
  kbd,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  kbd?: string;
}) {
  return (
    <div className="mb-1 flex items-center gap-2 border-b border-border/60 px-2 pb-1.5">
      <Search className="size-3.5 shrink-0 text-muted-foreground" />
      <input
        value={value}
        autoFocus
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Escape" && value) {
            e.preventDefault();
            onChange("");
          }
        }}
        className="h-6 flex-1 bg-transparent type-body outline-none placeholder:text-muted-foreground"
      />
      {value ? (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => onChange("")}
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="size-3" />
        </button>
      ) : kbd ? (
        <kbd className="rounded border border-border/60 px-1 type-subtext text-muted-foreground">
          {kbd}
        </kbd>
      ) : null}
    </div>
  );
}

/** A value list this long or longer gets its own search box. Five, not ten:
 *  a status list is exactly where you want to type "rev" instead of aiming. */
const VALUE_SEARCH_AT = 5;

function match(options: ViewMenuOption[], q: string) {
  const needle = q.trim().toLowerCase();
  if (!needle) return options;
  return options.filter((o) =>
    `${o.label} ${o.keywords ?? ""}`.toLowerCase().includes(needle),
  );
}

/* ================================================================= FilterMenu */

interface FilterFieldBase {
  id: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  /** Hide without disturbing the caller's array shape. */
  visible?: boolean;
  /** Start a new separated block above this field. */
  startsGroup?: boolean;
  /** Force the value search box on/off. Default: on at >= VALUE_SEARCH_AT options. */
  searchable?: boolean;
}

/** Multi-value field — checkboxes, the default filter shape (Status, Labels). */
export interface FilterMultiField extends FilterFieldBase {
  kind: "multi";
  options: ViewMenuOption[];
  values: string[];
  onChange: (values: string[]) => void;
  /**
   * The set the surface LANDS on. While `values` equals it, the field counts as
   * unfiltered: no chip in the bar, no dot on the funnel. Without this a page
   * whose default is a non-empty set (Issues lands on the four open statuses)
   * would permanently claim to be filtered, and the bar — which exists to
   * reflect the user's CHOICES — would never be empty.
   */
  defaultValues?: string[];
}

/** Single-value field — radios (Project, Cycle). */
export interface FilterSingleField extends FilterFieldBase {
  kind: "single";
  options: ViewMenuOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  /** Label for the clear row. Default "Any". */
  clearLabel?: string;
  /**
   * Offer the clear row at all. Default true. Set false when the options are
   * EXHAUSTIVE and one of them is always in force (a lifecycle slice: Open /
   * Settled / All) — there "any" isn't a state the list can be in, so the row
   * would be a control that does nothing.
   */
  clearable?: boolean;
}

/** Field with no values — a bare predicate row (Auto-closed). */
export interface FilterToggleField extends FilterFieldBase {
  kind: "toggle";
  checked: boolean;
  onChange: (checked: boolean) => void;
}

/** Escape hatch — you render the submenu body (a date range, a calendar). */
export interface FilterCustomField extends FilterFieldBase {
  kind: "custom";
  /** Right-aligned read-out of the current value on the field row. */
  summary?: string;
  /** Whether this field counts as applied (drives the dot + Reset). */
  active?: boolean;
  /** How the filter BAR removes this field's chip. Without it the chip renders
   *  read-only, since the menu can't know how to unset a body it doesn't own. */
  onClear?: () => void;
  render: (ctx: { close: () => void }) => React.ReactNode;
  widthPx?: number;
}

export type FilterField =
  | FilterMultiField
  | FilterSingleField
  | FilterToggleField
  | FilterCustomField;

export interface FilterMenuProps {
  fields: FilterField[];
  /** Cleared when the user picks "Reset filters" (shown only when something is applied). */
  onReset?: () => void;
  resetLabel?: string;
  /** Placeholder of the field search. */
  placeholder?: string;
  /** Hint key shown at the right of the search row. */
  kbd?: string;
  /** Open the funnel itself on hover, not just its submenus. Default false — a
   *  root that opens on pointer-over fires on every pass through the title row. */
  openOnHover?: boolean;
  align?: "start" | "center" | "end";
  widthPx?: number;
  trigger?: React.ReactNode;
  "aria-label"?: string;
}

/** Set equality, order-insensitive — a filter set is a set, not a sequence. */
export function sameValueSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const seen = new Set(b);
  return a.every((v) => seen.has(v));
}

function fieldApplied(f: FilterField): boolean {
  switch (f.kind) {
    case "multi":
      if (f.values.length === 0) return false;
      return !(f.defaultValues && sameValueSet(f.values, f.defaultValues));
    case "single":
      // A non-clearable single field is a MODE — one of its options is always in
      // force — so counting it would pin the applied dot on permanently.
      return f.clearable !== false && f.value != null;
    case "toggle":
      return f.checked;
    case "custom":
      return f.active === true;
  }
}

function fieldSummary(f: FilterField): string | undefined {
  switch (f.kind) {
    case "multi":
      if (f.values.length === 0) return undefined;
      if (f.values.length === 1)
        return f.options.find((o) => o.value === f.values[0])?.label ?? f.values[0];
      return `${f.values.length} selected`;
    case "single":
      return f.value == null
        ? undefined
        : (f.options.find((o) => o.value === f.value)?.label ?? f.value);
    case "custom":
      return f.summary;
    case "toggle":
      return undefined;
  }
}

/** A hover-opening submenu shell. Owns the value search + the scroll box. */
function FieldSub({
  field,
  children,
  options,
  widthPx = 220,
}: {
  field: FilterField;
  options?: ViewMenuOption[];
  widthPx?: number;
  children: (matches: ViewMenuOption[]) => React.ReactNode;
}) {
  const [q, setQ] = React.useState("");
  const showSearch =
    field.searchable ?? (options != null && options.length >= VALUE_SEARCH_AT);
  const matches = options ? match(options, q) : [];
  const summary = fieldSummary(field);

  return (
    <M.Sub onOpenChange={(open) => !open && setQ("")}>
      <M.SubTrigger className={ROW}>
        <Glyph icon={field.icon} />
        <span className="truncate">{field.label}</span>
        {summary ? <Summary>{summary}</Summary> : null}
        <ChevronRight
          className={cn(
            "size-3.5 shrink-0 text-muted-foreground",
            !summary && "ml-auto",
          )}
        />
      </M.SubTrigger>
      <M.Portal>
        <Elevated offset={2} shadowLevel={5} asChild>
          <M.SubContent
            sideOffset={2}
            alignOffset={-4}
            className={SURFACE}
            style={{ minWidth: widthPx }}
          >
            {showSearch ? (
              <MenuSearch value={q} onChange={setQ} placeholder="Filter…" />
            ) : null}
            <div className="max-h-72 overflow-y-auto">
              {children(matches)}
              {options != null && matches.length === 0 ? (
                <p className="px-2 py-1.5 type-body text-muted-foreground">
                  No matches.
                </p>
              ) : null}
            </div>
          </M.SubContent>
        </Elevated>
      </M.Portal>
    </M.Sub>
  );
}

function MultiField({ field }: { field: FilterMultiField }) {
  const set = new Set(field.values);
  const toggle = (value: string) =>
    field.onChange(
      set.has(value)
        ? field.values.filter((v) => v !== value)
        : [...field.values, value],
    );

  return (
    <FieldSub field={field} options={field.options}>
      {(matches) => (
        <>
          {matches.map((o) => (
            <M.CheckboxItem
              key={o.value}
              checked={set.has(o.value)}
              // Multi-select stays open across picks — the point of the hover
              // model is a stream of choices without re-opening per click.
              onSelect={(e) => e.preventDefault()}
              onCheckedChange={() => toggle(o.value)}
              className={cn(ROW, "pl-7")}
            >
              <CheckBox checked={set.has(o.value)} />
              <Glyph icon={o.icon} mark={o.mark} />
              <span className="truncate">{o.label}</span>
              {o.hint ? <Summary>{o.hint}</Summary> : null}
            </M.CheckboxItem>
          ))}
          {field.values.length > 0 ? (
            <>
              <M.Separator className="my-1 h-px bg-border" />
              <M.Item
                onSelect={(e) => {
                  e.preventDefault();
                  field.onChange([]);
                }}
                className={cn(ROW, "text-muted-foreground")}
              >
                Clear {field.label.toLowerCase()}
              </M.Item>
            </>
          ) : null}
        </>
      )}
    </FieldSub>
  );
}

function SingleField({ field }: { field: FilterSingleField }) {
  return (
    <FieldSub field={field} options={field.options}>
      {(matches) => (
        <M.RadioGroup
          value={field.value ?? ""}
          onValueChange={(v) => field.onChange(v === "" ? null : v)}
        >
          {field.clearable === false ? null : (
            <M.RadioItem value="" className={ROW}>
              <span className="text-muted-foreground">
                {field.clearLabel ?? "Any"}
              </span>
              <M.ItemIndicator className="ml-auto">
                <Check className="size-3.5" />
              </M.ItemIndicator>
            </M.RadioItem>
          )}
          {matches.map((o) => (
            <M.RadioItem key={o.value} value={o.value} className={ROW}>
              <Glyph icon={o.icon} mark={o.mark} />
              <span className="truncate">{o.label}</span>
              {o.hint ? <Summary>{o.hint}</Summary> : null}
              <M.ItemIndicator className={cn(o.hint ? "ml-1" : "ml-auto")}>
                <Check className="size-3.5" />
              </M.ItemIndicator>
            </M.RadioItem>
          ))}
        </M.RadioGroup>
      )}
    </FieldSub>
  );
}

export function FilterMenu({
  fields,
  onReset,
  resetLabel = "Reset filters",
  placeholder = "Add filter…",
  kbd = "F",
  openOnHover = false,
  align = "end",
  widthPx = 248,
  trigger,
  "aria-label": ariaLabel = "Filter",
}: FilterMenuProps) {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const close = React.useCallback(() => setOpen(false), []);

  React.useEffect(() => {
    if (!open) setQ("");
  }, [open]);

  const visible = fields.filter((f) => f.visible !== false);
  const appliedCount = visible.filter(fieldApplied).length;

  const needle = q.trim().toLowerCase();
  const shown = needle
    ? visible.filter((f) => f.label.toLowerCase().includes(needle))
    : visible;

  const triggerNode = trigger ?? (
    <Button variant="flat" size="icon-tb" aria-label={ariaLabel} className="relative">
      <Filter className="size-3.5" />
      {appliedCount > 0 ? (
        <span
          aria-hidden
          className="absolute right-1 top-1 size-1.5 rounded-full bg-foreground"
        />
      ) : null}
    </Button>
  );

  return (
    <M.Root open={open} onOpenChange={setOpen} modal={false}>
      <M.Trigger
        asChild
        onPointerEnter={openOnHover ? () => setOpen(true) : undefined}
      >
        {triggerNode}
      </M.Trigger>
      <M.Portal>
        <Elevated offset={2} shadowLevel={5} asChild>
          <M.Content
            align={align}
            sideOffset={4}
            className={SURFACE}
            style={{ minWidth: widthPx }}
          >
            <MenuSearch
              value={q}
              onChange={setQ}
              placeholder={placeholder}
              kbd={kbd}
            />

            {shown.map((field, i) => (
              <React.Fragment key={field.id}>
                {/* A field can open a new block; the search collapses the blocks
                    back into one flat result list, as a command palette should. */}
                {field.startsGroup && i > 0 && !needle ? (
                  <M.Separator className="my-1 h-px bg-border" />
                ) : null}
                {field.kind === "multi" ? <MultiField field={field} /> : null}
                {field.kind === "single" ? <SingleField field={field} /> : null}
                {field.kind === "custom" ? (
                  <FieldSub field={field} widthPx={field.widthPx ?? 240}>
                    {() => <div className="p-1">{field.render({ close })}</div>}
                  </FieldSub>
                ) : null}
                {field.kind === "toggle" ? (
                  <M.CheckboxItem
                    checked={field.checked}
                    onSelect={(e) => e.preventDefault()}
                    onCheckedChange={(c) => field.onChange(c === true)}
                    className={cn(ROW, "pl-7")}
                  >
                    <CheckBox checked={field.checked} />
                    <Glyph icon={field.icon} />
                    <span className="truncate">{field.label}</span>
                  </M.CheckboxItem>
                ) : null}
              </React.Fragment>
            ))}

            {shown.length === 0 ? (
              <p className="px-2 py-1.5 type-body text-muted-foreground">
                No filters match.
              </p>
            ) : null}

            {onReset && appliedCount > 0 ? (
              <>
                <M.Separator className="my-1 h-px bg-border" />
                <M.Item
                  onSelect={() => onReset()}
                  className={cn(ROW, "text-muted-foreground")}
                >
                  <X className="size-3.5" />
                  <span>{resetLabel}</span>
                  <Summary>{appliedCount}</Summary>
                </M.Item>
              </>
            ) : null}
          </M.Content>
        </Elevated>
      </M.Portal>
    </M.Root>
  );
}

/* ================================================================ DisplayMenu */

export interface DisplayViewOption {
  value: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
}

interface DisplayControlBase {
  id: string;
  label: string;
  visible?: boolean;
  disabled?: boolean;
}

/** `Label ………… [Value ▾]` — the panel's workhorse row (Grouping, Ordering). */
export interface DisplaySelectControl extends DisplayControlBase {
  kind: "select";
  options: ViewMenuOption[];
  value: string;
  onChange: (value: string) => void;
  /** Leading glyph between label and control (Linear's sort-direction affordance). */
  adornment?: React.ReactNode;
  widthPx?: number;
}

/** `Label ………… (switch)` — a binary display setting. */
export interface DisplaySwitchControl extends DisplayControlBase {
  kind: "switch";
  checked: boolean;
  onChange: (checked: boolean) => void;
}

/** A wrapping grid of on/off pills — "Display properties". */
export interface DisplayChipsControl extends DisplayControlBase {
  kind: "chips";
  options: ViewMenuOption[];
  values: string[];
  onChange: (values: string[]) => void;
}

export type DisplayControl =
  | DisplaySelectControl
  | DisplaySwitchControl
  | DisplayChipsControl;

export interface DisplaySection {
  id: string;
  /** Heading above the block (Linear labels only the last one, "List options"). */
  label?: string;
  controls: DisplayControl[];
  visible?: boolean;
}

export interface DisplayMenuProps {
  /** The segmented view switch pinned to the top of the panel. */
  views?: {
    options: DisplayViewOption[];
    value: string;
    onChange: (value: string) => void;
  };
  sections: DisplaySection[];
  openOnHover?: boolean;
  align?: "start" | "center" | "end";
  widthPx?: number;
  trigger?: React.ReactNode;
  "aria-label"?: string;
}

/** The panel's top segmented control — big targets, the primary decision. */
function ViewSegments({
  options,
  value,
  onChange,
}: {
  options: DisplayViewOption[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex items-center gap-1.5 p-1">
      {options.map((o) => {
        const Icon = o.icon;
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(o.value)}
            className={cn(
              // Alpha fills, not surface tokens: inside a floating panel the
              // surface scale has already run out (surface-3 == the popover's own
              // background in light mode), so a token fill would be invisible.
              "flex h-8 flex-1 items-center justify-center gap-2 rounded-md type-body transition-colors",
              active
                ? "bg-foreground/[0.07] font-medium text-foreground"
                : "text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground",
            )}
          >
            {Icon ? <Icon className="size-3.5" /> : null}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function ControlRow({ control }: { control: DisplayControl }) {
  if (control.kind === "chips") {
    return (
      <div className="px-2 py-1.5">
        <p className="pb-1.5 type-body text-muted-foreground">{control.label}</p>
        <div className="flex flex-wrap gap-1.5">
          {control.options.map((o) => {
            const on = control.values.includes(o.value);
            return (
              <button
                key={o.value}
                type="button"
                aria-pressed={on}
                onClick={() =>
                  control.onChange(
                    on
                      ? control.values.filter((v) => v !== o.value)
                      : [...control.values, o.value],
                  )
                }
                className={cn(
                  "h-6 rounded-full px-2.5 type-subtext transition-colors",
                  on
                    ? "bg-foreground/[0.10] text-foreground"
                    : "text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground",
                )}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        // h-9, not h-8: the pill control is h-7, and a 32px row leaves it looking
        // wedged. One row height for both control kinds keeps the column even.
        "flex h-9 items-center gap-2 px-2",
        control.disabled && "opacity-50",
      )}
    >
      <span className="truncate type-body">{control.label}</span>
      <div className="ml-auto flex items-center gap-2">
        {control.kind === "select" ? (
          <>
            {control.adornment}
            <Select
              value={control.value}
              onValueChange={control.onChange}
              disabled={control.disabled}
            >
              <SelectTrigger variant="pill" aria-label={control.label}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {control.options.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.icon ? <o.icon className="size-3.5" /> : null}
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        ) : (
          <Switch
            size="sm"
            checked={control.checked}
            onCheckedChange={control.onChange}
            disabled={control.disabled}
            aria-label={control.label}
          />
        )}
      </div>
    </div>
  );
}

export function DisplayMenu({
  views,
  sections,
  openOnHover = false,
  align = "end",
  widthPx = 300,
  trigger,
  "aria-label": ariaLabel = "Display options",
}: DisplayMenuProps) {
  const [open, setOpen] = React.useState(false);

  const visible = sections
    .filter((s) => s.visible !== false)
    .map((s) => ({ ...s, controls: s.controls.filter((c) => c.visible !== false) }))
    .filter((s) => s.controls.length > 0);

  const triggerNode = trigger ?? (
    <Button variant="flat" size="icon-tb" aria-label={ariaLabel}>
      <SlidersHorizontal className="size-3.5" />
    </Button>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        asChild
        onPointerEnter={openOnHover ? () => setOpen(true) : undefined}
      >
        {triggerNode}
      </PopoverTrigger>
      <PopoverContent align={align} className="p-1" style={{ width: widthPx }}>
        {views ? (
          <>
            <ViewSegments {...views} />
            <div className="my-1 h-px bg-border" />
          </>
        ) : null}
        {visible.map((section, i) => (
          <React.Fragment key={section.id}>
            {i > 0 ? <div className="my-1 h-px bg-border" /> : null}
            {section.label ? (
              <p className="px-2 pb-0.5 pt-1.5 type-body font-medium">{section.label}</p>
            ) : null}
            {section.controls.map((c) => (
              <ControlRow key={c.id} control={c} />
            ))}
          </React.Fragment>
        ))}
      </PopoverContent>
    </Popover>
  );
}
